import { setCookie } from "@insite/client-framework/Common/Cookies";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import setPLPRendering from "@ucs/Handlers/SetPLPRendering";
import ChevronDown from "@ucs/Icons/ChevronDown";
import ChevronLeft from "@ucs/Icons/ChevronSmallLeft";
import ChevronRight from "@ucs/Icons/ChevronSmallRight";
import DoubleChevronLeft from "@ucs/Icons/DoubleChevronLeft";
import DoubleChevronRight from "@ucs/Icons/DoubleChevronRight";
import Pagination, { PaginationPresentationProps } from "@ucs/MobiusOverrides/Pagination/Pagination";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    scrollToTop = "scrollToTop",
    hideOnMobile = "hideOnMobile",
    hidePagination = "hidePagination",
    hideResultsPerPage = "hideResultsPerPage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.scrollToTop]: boolean;
        [fields.hideOnMobile]: boolean;
        [fields.hidePagination]: boolean;
        [fields.hideResultsPerPage]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
    setPLPRendering,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    pagination: {
        navIconsSrc: {
            firstPage: DoubleChevronLeft,
            nextPage: ChevronRight,
            lastPage: DoubleChevronRight,
            previousPage: ChevronLeft,
        },
        cssOverrides: {
            perPageSelect: css`
                flex: 1 1;
            `,
            linkList: css`
                flex: 1 1;
                text-align: right;
                width: auto;
                ul {
                    display: inline-flex;
                    flex-wrap: nowrap;
                    svg {
                        color: ${UCSColors.gray05};
                    }
                    li {
                        &:nth-child(1),
                        &:nth-child(2),
                        &:nth-last-child(1),
                        &:nth-last-child(2) {
                            button {
                                border: none;
                            }
                        }
                        &:first-child,
                        &:last-child {
                            span {
                                height: 25px;
                                width: 30px;
                            }
                            svg {
                                height: auto;
                                width: 30px;
                            }
                        }
                        &:nth-child(2),
                        &:nth-last-child(2) {
                            span {
                                height: 25px;
                                width: 23px;
                            }
                            svg {
                                height: auto;
                                width: 23px;
                            }
                        }
                    }
                    button {
                        &:disabled {
                            opacity: 0.25;
                        }
                    }
                }
            `,
            pagination: css`
                margin: 0px 15px !important;
                margin-top: 30px !important;
                gap: 22px 30px;
            `,
            currentButton: css`
                background-color: ${UCSColors.gray03};
                &:not(:disabled):hover {
                    background-color: ${UCSColors.gray03};
                }
                color: white;
            `,
        },
        buttonProps: {
            css: css`
                font-size: 14px;
                font-weight: 700;
                height: 30px;
                min-width: 30px;
                min-height: unset;
                border: 2px solid ${UCSColors.gray07};
                color: ${UCSColors.gray04};
            `,
        },
        selectProps: {
            iconProps: {
                src: ChevronDown,
                color: UCSColors.gray05,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                    color: black;
                    label {
                        text-overflow: clip;
                        padding: 0px;
                        margin-right: 7px;
                        font-size: 16px !important;
                        color: black !important;
                        font-weight: 400;
                        width: auto;
                        text-transform: inherit;
                    }
                    select {
                        width: auto;
                        background-color: transparent !important;
                        border: none !important;
                        font-size: 16px !important;
                        color: black !important;
                        margin: 0px !important;
                        padding: 0px !important;
                        padding-right: 16px !important;
                        height: auto !important;
                    }
                    span {
                        background-color: transparent !important;
                        padding: 0px !important;
                        right: 0px !important;
                        width: 16px !important;
                        height: 21px !important;
                    }
                `,
                formInputWrapper: css`
                    width: auto;
                    margin-top: 3px !important;
                `,
            },
        },
    },
};

const styles = paginationStyles;

const ProductListPagination = ({ addProductFilters, hidePagination, pagination, fields, setPLPRendering }: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);
    const changePage = (newPageIndex: number) => {
        const gridElement = document!.getElementById("productGridWrapper");
        if (gridElement) {
            const y = gridElement.getBoundingClientRect().height;
            setPLPRendering({ gridHeight: y });
        }
        addProductFilters({
            page: newPageIndex,
            onComplete() {
                if (fields.scrollToTop) {
                    const paginationEle = document!.getElementById("paginationTop");
                    if (paginationEle) {
                        const y = paginationEle.getBoundingClientRect().top + window.pageYOffset - 140;
                        setTimeout(() => {
                            window.scrollTo({ top: y, behavior: "smooth" });
                        }, 500);
                        setPLPRendering({ gridHeight: 0 });
                    }
                }
            },
        });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    return fields.hideOnMobile ? (
        <Hidden below="sm">
            <Pagination
                {...styles.pagination}
                resultsCount={pagination!.totalItemCount}
                currentPage={pagination!.page}
                resultsPerPage={pagination!.pageSize}
                resultsPerPageOptions={pagination!.pageSizeOptions}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
                pageSizeCookie="ProductList-PageSize"
                data-test-selector="productList_pagination"
                resultsPerPageLabel="Items/page: "
                hidePagination={fields.hidePagination || false}
                hideResultsPerPage={fields.hideResultsPerPage || false}
            />
        </Hidden>
    ) : (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination!.totalItemCount}
            currentPage={pagination!.page}
            resultsPerPage={pagination!.pageSize}
            resultsPerPageOptions={pagination!.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="ProductList-PageSize"
            data-test-selector="productList_pagination"
            resultsPerPageLabel="Items/page: "
            hidePagination={fields.hidePagination || false}
            hideResultsPerPage={fields.hideResultsPerPage || false}
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.scrollToTop,
                displayName: "Scroll to top",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.hideOnMobile,
                displayName: "Hide on mobile",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.hidePagination,
                displayName: "Hide Pagination",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.hideResultsPerPage,
                displayName: "Hide Results Per Page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
